import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnDestroy,
  Optional,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MessageService } from '../../services/message-service.service';

class CustomFieldErrorMatcher implements ErrorStateMatcher {
  constructor(private customControl: FormControl) { }
  isErrorState(): boolean {
    return this.customControl && this.customControl.touched && (this.customControl.invalid);
  }
}

@Component({
  selector: 'app-textarea-form',
  templateUrl: './app-textarea-form.component.html',
  styleUrls: ['./app-textarea-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class AppTextareaFormComponent implements ControlValueAccessor, AfterViewInit, DoCheck, OnDestroy {
  @ViewChild(MatInput) textarea: ElementRef<HTMLTextAreaElement>;
  @Input() public placeholder: string;
  @Input() public showLabel?: boolean;
  @Input() public testId: string;
  @Input() public inputLabel?: string;
  @Input() public inputHint?: string;
  @Input() public inputHintIcon?: boolean;
  @Input() public inputClass?: string;
  @Input() public warningMessage?: string;
  @Input() public isWarningVisible?: string;
  @Input() public autoFocus?: boolean;

  @Input()
  get isRequired(): boolean { return this._required; }
  set isRequired(value: boolean) { this._required = value; }
  @Input()
  get readonly(): boolean { return this._readonly; }
  set readonly(value: boolean) { this._readonly = value; }
  @Input()
  get maxLength(): number { return this._maxLength || null; }
  set maxLength(value: number) { this._maxLength = value; }

  // tslint:disable-next-line:variable-name
  private _required: boolean; private _readonly: boolean; private _maxLength: number;

  public focused = false;
  private oldDisabled = null;
  private oldTouched = null;
  private oldControl: FormControl = null;
  private ngUnsubscribe = new Subject<void>();

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(private messageService: MessageService, private cd: ChangeDetectorRef,
              private fm: FocusMonitor, private elRef: ElementRef<HTMLElement>,
              @Self() @Optional() public ctrl: NgControl) {
    if (this.ctrl) {
      this.ctrl.valueAccessor = this;
    }

    fm.monitor(elRef.nativeElement, true).pipe(takeUntil(this.ngUnsubscribe)).subscribe(origin => {
      this.focused = !!origin;
      this.cd.markForCheck();
    });
  }

  ngAfterViewInit() {
    if (this.autoFocus) {
      setTimeout(() => {
        this.fm.focusVia(this.textarea, 'program');
        this.fm.stopMonitoring(this.elRef.nativeElement); }, 0);
    }
  }

  ngDoCheck() {
    if (this.ctrl.disabled !== this.oldDisabled) {
      this.oldDisabled = this.ctrl.disabled;
      this.cd.markForCheck();
    }
    if (this.ctrl.touched !== this.oldTouched) {
      this.oldTouched = this.ctrl.touched;
      this.cd.markForCheck();
    }
    if (this.ctrl.control !== this.oldControl) {
      this.oldTouched = this.ctrl.touched;
      this.cd.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  public errorMatcher() {
    return new CustomFieldErrorMatcher(this.ctrl.control as FormControl);
  }

  public getErrorMessage(): string {
    if (this.ctrl?.invalid) {
      return this.messageService.getControlErrorMessage(this.ctrl.errors);
    }  return null;
  }

  writeValue(value: any): void {}

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
